import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getApiBaseUrl } from '../config/apiBase';
import { adminFetchInit, adminStreamUrl } from '../config/adminApi';
import DockerHeader from '../components/admin/DockerHeader';
import LogConsole from '../components/admin/LogConsole';
import ContainerStats from '../components/admin/ContainerStats';
import OpenSanctionsSyncMonitor from '../components/admin/OpenSanctionsSyncMonitor';
import { buildFeatureRequestCounts, resolveSearchBucket } from './admin/adminAnalyticsUtils';
import AdminSearchDetailDrawer from './admin/AdminSearchDetailDrawer';
import AdminOllamaPanel from './admin/AdminOllamaPanel';
import AdminFrontendSearchTraces from './admin/AdminFrontendSearchTraces';
import AdminNlpLab from './admin/AdminNlpLab';
import AdminReadabilityPanel from './admin/AdminReadabilityPanel';
import AdminToolsHealthPanel from './admin/AdminToolsHealthPanel';
import AdminContainerControls from './admin/AdminContainerControls';
import AdminParallelSearchesCard from './admin/AdminParallelSearchesCard';
import AdminHtopPanel from './admin/AdminHtopPanel';
import AdminNginxPanel from './admin/AdminNginxPanel';
import {
  ADMIN_BTN_GHOST,
  ADMIN_BTN_PRIMARY,
  ADMIN_CARD,
  ADMIN_CARD_MUTED,
  ADMIN_CHART_COLORS,
  ADMIN_SECTION_TITLE,
  ADMIN_SHELL,
  ADMIN_TAB_TRIGGER,
} from './admin/adminTheme';
const ADMIN_CONTAINER_LOG_TAIL = 180;
const ADMIN_CONTAINER_LOG_CAP = 2500;

const SERVICE_ICON = {
  backend: '🧠',
  redis: '🧱',
  worker: '⚙️',
  telegram_bot: '🤖',
  frontend: '🖥️',
  ollama: '🦙',
};

function StatusPill({ status }) {
  const s = String(status || '').toLowerCase();
  const tone =
    s === 'up' || s === 'configured'
      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/35'
      : s === 'degraded' || s === 'unknown'
        ? 'bg-amber-500/15 text-amber-200 border-amber-400/35'
        : 'bg-red-500/15 text-red-300 border-red-400/35';
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${tone}`}>{status}</span>;
}

function ApiCheckPill({ ok }) {
  return (
    <span
      className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${
        ok ? 'border-emerald-400/35 bg-emerald-500/15 text-emerald-300' : 'border-red-400/35 bg-red-500/15 text-red-300'
      }`}
    >
      {ok ? 'OK' : 'KO'}
    </span>
  );
}

function ApiQuotaCell({ quota, provider }) {
  if (provider === 'HIBP_API_KEY' || provider === 'VT_API_KEY') {
    return <span className="font-mono text-[11px] text-slate-400">N/A</span>;
  }
  if (!quota || typeof quota.remaining !== 'number') {
    return <span className="font-mono text-[11px] text-slate-400">No Signal</span>;
  }

  const remaining = Number(quota.remaining);
  const total = quota.total != null ? Number(quota.total) : null;
  const unit = String(quota.unit || '').trim();
  const totalLabel = total != null && Number.isFinite(total) ? total.toLocaleString() : '∞';
  const label = `${remaining.toLocaleString()} / ${totalLabel}${unit ? ` ${unit}` : ''}`;
  const isLow = total != null && total > 0 && remaining / total < 0.1;

  return (
    <span
      className={`font-mono text-[11px] tabular-nums ${
        isLow ? 'font-semibold text-rose-300' : 'text-slate-300'
      }`}
      title={isLow ? 'Less than 10% quota remaining — consider topping up' : undefined}
    >
      {label}
    </span>
  );
}

function apiCheckLatencyLabel(check) {
  if (check?.latency_ms != null && Number.isFinite(Number(check.latency_ms))) {
    return `${Math.round(Number(check.latency_ms))} ms`;
  }
  if (check?.http != null) {
    return `HTTP ${check.http}`;
  }
  return '—';
}

function formatUptimeSeconds(sec) {
  if (sec == null || !Number.isFinite(Number(sec))) return '-';
  const s = Math.max(0, Math.floor(Number(sec)));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const TAB_TRIGGER = ADMIN_TAB_TRIGGER;

const CHART_COLORS = ADMIN_CHART_COLORS;

function DashboardAreaChart({ title, dataKey, series, stroke }) {
  return (
    <div className="flex min-h-[230px] flex-1 flex-col rounded-xl border border-white/10 bg-gradient-to-br from-slate-950/80 via-slate-900/60 to-slate-950/40 p-4 shadow-inner shadow-black/20">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">{title}</h3>
        <span className="text-[10px] font-semibold text-slate-500">0–100%</span>
      </div>
      <div className="min-h-[190px] flex-1 w-full min-w-0">
        {series.length === 0 ? (
          <div className="flex h-[190px] items-center justify-center rounded-lg border border-dashed border-white/10 bg-slate-950/55/80 text-xs font-semibold text-slate-400">
            Collecting samples (polling)…
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={190}>
            <AreaChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={stroke} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={stroke} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
              <YAxis domain={[0, 100]} width={36} tick={{ fontSize: 10, fill: '#64748b' }} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                formatter={(v) => [`${Number(v).toFixed(1)}%`, title]}
              />
              <Area
                type="monotone"
                dataKey={dataKey}
                stroke={stroke}
                strokeWidth={2}
                fill={`url(#grad-${dataKey})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function HostMetricBar({ label, pct, detail }) {
  const raw = Number(pct);
  const p = Number.isFinite(raw) ? Math.min(100, Math.max(0, raw)) : 0;
  const barColor = p >= 90 ? 'bg-red-500' : p >= 75 ? 'bg-amber-500' : 'bg-cyan-500';
  return (
    <div className="rounded-lg border border-white/10 bg-slate-900/55 p-2">
      <div className="mb-1 flex justify-between gap-2 text-[10px] font-black uppercase tracking-wider text-slate-500">
        <span>{label}</span>
        <span className="shrink-0 text-right text-[10px] font-bold normal-case tracking-normal text-slate-400">
          {detail}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-800">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

/** After a successful clear/purge, grey out that control briefly (no confirm dialogs). */
const DESTRUCTIVE_COOLDOWN_MS = 8000;

const DESTRUCTIVE_BTN =
  'rounded-lg border px-3 py-2 text-[11px] font-black uppercase tracking-wider transition disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40 disabled:grayscale';

export default function AdminPage() {
  const base = getApiBaseUrl();
  const [overview, setOverview] = useState({ services: [], resource: {} });
  const [checks, setChecks] = useState({});
  const [searches, setSearches] = useState([]);
  const [selectedSearch, setSelectedSearch] = useState(null);
  const [actionToast, setActionToast] = useState('');
  const [activeLogTab, setActiveLogTab] = useState('backend');
  const [containerLogs, setContainerLogs] = useState({});
  const [logStreamStatus, setLogStreamStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cacheEnabled, setCacheEnabled] = useState(true);
  const [destructiveFlight, setDestructiveFlight] = useState(null);
  const [destructiveCooldownUntil, setDestructiveCooldownUntil] = useState({});
  const [oauth, setOauth] = useState({
    loading: true,
    configured: false,
    authenticated: false,
    email: null,
  });
  const destructiveCooldownTimersRef = useRef({});
  const logBoxRef = useRef(null);
  const logBufferRef = useRef({});
  const logFlushRafRef = useRef(null);
  const logStreamOpenCountRef = useRef({});
  const logServices = useMemo(
    () => ['backend', 'ollama', 'redis', 'worker', 'beat', 'telegram_bot', 'frontend', 'traffic'],
    []
  );

  const [mainTab, setMainTab] = useState('overview');
  const [metricSeries, setMetricSeries] = useState([]);
  const [logFollowScroll, setLogFollowScroll] = useState(true);

  useEffect(() => {
    return () => {
      Object.values(destructiveCooldownTimersRef.current).forEach(clearTimeout);
      destructiveCooldownTimersRef.current = {};
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${base}/admin/oauth/status`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setOauth({
          loading: false,
          configured: Boolean(data?.configured),
          authenticated: Boolean(data?.authenticated),
          email: typeof data?.email === 'string' ? data.email : null,
        });
      } catch {
        if (!cancelled) setOauth((prev) => ({ ...prev, loading: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [base]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search || '');
    const oerr = (params.get('admin_oauth_error') || '').trim().toLowerCase();
    if (!oerr) return;
    const msgByCode = {
      not_authorized: 'You are not authorized to access this console.',
      forbidden: 'You are not authorized to access this console.',
      access_denied: 'Google sign-in was cancelled or denied.',
      token: 'Google sign-in failed (token). Please try again.',
      userinfo: 'Google sign-in failed (profile). Please try again.',
      no_token: 'Google sign-in failed (no token). Please try again.',
    };
    setError(msgByCode[oerr] || `Google sign-in failed (${oerr}).`);
  }, []);

  const isDestructiveLocked = useCallback(
    (key) => {
      if (destructiveFlight === key) return true;
      const until = destructiveCooldownUntil[key];
      return typeof until === 'number' && Date.now() < until;
    },
    [destructiveCooldownUntil, destructiveFlight]
  );

  const armDestructiveCooldown = useCallback((key) => {
    const until = Date.now() + DESTRUCTIVE_COOLDOWN_MS;
    setDestructiveCooldownUntil((prev) => ({ ...prev, [key]: until }));
    const prevT = destructiveCooldownTimersRef.current[key];
    if (prevT) clearTimeout(prevT);
    destructiveCooldownTimersRef.current[key] = setTimeout(() => {
      setDestructiveCooldownUntil((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
      delete destructiveCooldownTimersRef.current[key];
    }, DESTRUCTIVE_COOLDOWN_MS);
  }, []);

  const scheduleLogFlush = useCallback(() => {
    if (logFlushRafRef.current != null) return;
    logFlushRafRef.current = requestAnimationFrame(() => {
      logFlushRafRef.current = null;
      const batch = logBufferRef.current;
      const keys = Object.keys(batch);
      if (!keys.length) return;
      logBufferRef.current = {};
      setContainerLogs((prev) => {
        const next = { ...prev };
        for (const svc of keys) {
          const arr = batch[svc];
          if (!arr?.length) continue;
          const cur = next[svc] || [];
          next[svc] = [...cur, ...arr].slice(-ADMIN_CONTAINER_LOG_CAP);
        }
        return next;
      });
    });
  }, []);

  const appendLogLines = useCallback(
    (svc, lines) => {
      if (!svc || !lines?.length) return;
      if (!logBufferRef.current[svc]) logBufferRef.current[svc] = [];
      logBufferRef.current[svc].push(...lines);
      scheduleLogFlush();
    },
    [scheduleLogFlush]
  );

  useEffect(() => {
    const root = document.getElementById('root');
    const prev = {
      htmlOverflowY: document.documentElement.style.overflowY,
      bodyOverflowY: document.body.style.overflowY,
      rootOverflowY: root?.style.overflowY || '',
      rootMaxHeight: root?.style.maxHeight || '',
      rootHeight: root?.style.height || '',
    };

    document.documentElement.style.overflowY = 'auto';
    document.body.style.overflowY = 'auto';
    if (root) {
      root.style.overflowY = 'auto';
      root.style.maxHeight = 'none';
      root.style.height = 'auto';
    }

    return () => {
      document.documentElement.style.overflowY = prev.htmlOverflowY;
      document.body.style.overflowY = prev.bodyOverflowY;
      if (root) {
        root.style.overflowY = prev.rootOverflowY;
        root.style.maxHeight = prev.rootMaxHeight;
        root.style.height = prev.rootHeight;
      }
    };
  }, []);

  const loadOverview = useCallback(async () => {
    const res = await fetch(`${base}/admin/system-overview`, adminFetchInit());
    if (!res.ok) throw new Error(`system-overview HTTP ${res.status}`);
    const data = await res.json();
    setOverview(data || { services: [], resource: {} });
  }, [base]);

  const loadSearches = useCallback(async () => {
    const res = await fetch(`${base}/admin/last-searches`, adminFetchInit());
    if (!res.ok) throw new Error(`last-searches HTTP ${res.status}`);
    const data = await res.json();
    setSearches(Array.isArray(data?.items) ? data.items : []);
  }, [base]);

  const loadTrafficLogs = useCallback(async (limit = 350) => {
    const res = await fetch(`${base}/admin/traffic-log?limit=${limit}`, adminFetchInit());
    if (!res.ok) throw new Error(`traffic-log HTTP ${res.status}`);
    const data = await res.json();
    const lines = Array.isArray(data?.lines) ? data.lines : [];
    setContainerLogs((prev) => ({ ...prev, traffic: lines }));
    setLogStreamStatus((prev) => ({ ...prev, traffic: 'live' }));
  }, [base]);

  const loadCacheSettings = useCallback(async () => {
    const res = await fetch(`${base}/admin/cache/settings`, adminFetchInit());
    if (!res.ok) throw new Error(`cache-settings HTTP ${res.status}`);
    const data = await res.json();
    setCacheEnabled(Boolean(data?.enabled));
  }, [base]);

  const runApiChecks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${base}/admin/check-apis`, adminFetchInit());
      if (!res.ok) throw new Error(`check-apis HTTP ${res.status}`);
      const data = await res.json();
      setChecks(data?.checks || {});
    } catch (e) {
      const msg = String(e?.message || e);
      setError(msg === 'Failed to fetch' ? `${msg} — API: ${base} (check /health and that the backend is running)` : msg);
    } finally {
      setLoading(false);
    }
  }, [base]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadOverview(), loadSearches(), loadCacheSettings()]);
    } catch (e) {
      const msg = String(e?.message || e);
      setError(msg === 'Failed to fetch' ? `${msg} — API: ${base} (check /health and that the backend is running)` : msg);
    } finally {
      setLoading(false);
    }
  }, [base, loadCacheSettings, loadOverview, loadSearches]);

  const clearRedis = useCallback(
    async (scope = 'db') => {
      const key = scope === 'all' ? 'redis-all' : 'redis-db';
      if (isDestructiveLocked(key)) return;
      setDestructiveFlight(key);
      setLoading(true);
      setError('');
      try {
        const res = await fetch(
          `${base}/admin/redis/clear-cache?scope=${scope}&confirm=true`,
          adminFetchInit({ method: 'POST' })
        );
        if (!res.ok) throw new Error(`redis clear HTTP ${res.status}`);
        const data = await res.json();
        const deleted = Number(data?.deleted) || 0;
        const dbsize = data?.redis_meta?.dbsize ?? data?.dbsize ?? '—';
        setActionToast(
          scope === 'all'
            ? `Redis FLUSHALL: ${deleted} keys removed · DB size now ${dbsize}`
            : `Redis FLUSHDB: ${deleted} keys removed · DB size now ${dbsize}`
        );
        await refreshAll();
        armDestructiveCooldown(key);
      } catch (e) {
        setError(String(e?.message || e));
      } finally {
        setDestructiveFlight(null);
        setLoading(false);
      }
    },
    [armDestructiveCooldown, base, isDestructiveLocked, refreshAll]
  );

  const toggleGlobalCache = useCallback(async () => {
    const next = !cacheEnabled;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${base}/admin/cache/settings`,
        adminFetchInit({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled: next }),
        })
      );
      if (!res.ok) throw new Error(`cache toggle HTTP ${res.status}`);
      setCacheEnabled(next);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [base, cacheEnabled]);

  const purgeNamespace = useCallback(
    async (namespace) => {
      const key = `purge:${namespace}`;
      if (isDestructiveLocked(key)) return;
      setDestructiveFlight(key);
      setLoading(true);
      setError('');
      try {
        const res = await fetch(
          `${base}/admin/cache/purge-namespace`,
          adminFetchInit({
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ namespace }),
          })
        );
        if (!res.ok) throw new Error(`purge namespace HTTP ${res.status}`);
        const data = await res.json();
        const deleted = Number(data?.deleted) || 0;
        const dbsize = data?.redis_meta?.dbsize ?? data?.dbsize ?? '—';
        setActionToast(`Purged ${namespace}: ${deleted} keys deleted · DB size now ${dbsize}`);
        await refreshAll();
        armDestructiveCooldown(key);
      } catch (e) {
        setError(String(e?.message || e));
      } finally {
        setDestructiveFlight(null);
        setLoading(false);
      }
    },
    [armDestructiveCooldown, base, isDestructiveLocked, refreshAll]
  );

  useEffect(() => {
    if (!actionToast) return undefined;
    const t = setTimeout(() => setActionToast(''), 6500);
    return () => clearTimeout(t);
  }, [actionToast]);

  useEffect(() => {
    if (oauth.loading) return undefined;
    if (oauth.configured && !oauth.authenticated) return undefined;
    refreshAll();
    const t = setInterval(() => {
      loadOverview().catch(() => {});
      loadSearches().catch(() => {});
      loadCacheSettings().catch(() => {});
    }, 7000);
    return () => clearInterval(t);
  }, [
    loadCacheSettings,
    loadOverview,
    loadSearches,
    oauth.authenticated,
    oauth.configured,
    oauth.loading,
    refreshAll,
  ]);

  useEffect(() => {
    if (oauth.loading || (oauth.configured && !oauth.authenticated)) {
      return undefined;
    }
    if (mainTab !== 'logs') {
      return undefined;
    }
    let cancelled = false;
    const seedServices = logServices.filter((svc) => svc !== 'traffic');
    (async () => {
      await loadTrafficLogs(350).catch(() => {});
      await Promise.all(
        seedServices.map(async (svc) => {
          try {
            const res = await fetch(
              `${base}/admin/container-logs?service=${encodeURIComponent(svc)}&tail=100`,
              adminFetchInit(),
            );
            if (!res.ok || cancelled) return;
            const data = await res.json();
            const lines = Array.isArray(data?.lines) ? data.lines : [];
            if (!lines.length) return;
            setContainerLogs((prev) => {
              if ((prev[svc] || []).length > 0) return prev;
              return { ...prev, [svc]: lines.slice(-ADMIN_CONTAINER_LOG_CAP) };
            });
            setLogStreamStatus((prev) => ({
              ...prev,
              [svc]: prev[svc] === 'live' ? 'live' : 'idle',
            }));
          } catch {
            /* soft-fail per service */
          }
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [base, loadTrafficLogs, logServices, mainTab, oauth.authenticated, oauth.configured, oauth.loading]);

  /**
   * SSE log stream — open **only one** connection at a time (the active service tab).
   *
   * Opening one EventSource per service (6-7 streams) saturates Chrome's 6 per-host
   * HTTP/1.1 connection limit, which blocks every other fetch/SSE on the same origin
   * (search preprocess, scan SSE, health, client-search-trace, etc.).
   *
   * Fix: connect only to ``activeLogTab`` while on the Logs main-tab. When the user
   * switches sub-tab, the old stream is closed and a new one opens — at most 1 conn.
   */
  useEffect(() => {
    if (oauth.loading || (oauth.configured && !oauth.authenticated)) {
      return undefined;
    }
    if (mainTab !== 'logs') {
      return undefined;
    }
    const svc = activeLogTab;
    if (!svc || svc === 'traffic') {
      return undefined;
    }
    const url = adminStreamUrl(
      base,
      `/admin/container-logs/stream?service=${encodeURIComponent(svc)}&tail=${ADMIN_CONTAINER_LOG_TAIL}`
    );
    const es = new EventSource(url);
    es.onopen = () => {
      const n = (logStreamOpenCountRef.current[svc] || 0) + 1;
      logStreamOpenCountRef.current[svc] = n;
      if (n > 1) {
        delete logBufferRef.current[svc];
        setContainerLogs((prev) => ({ ...prev, [svc]: [] }));
      }
      setLogStreamStatus((prev) => ({ ...prev, [svc]: 'live' }));
    };
    es.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data || '{}');
        if (
          (msg.type === 'line' || msg.type === 'error') &&
          typeof msg.text === 'string'
        ) {
          appendLogLines(svc, [msg.text]);
        }
      } catch {
        appendLogLines(svc, [String(ev.data || '')]);
      }
    };
    es.onerror = () => {
      setLogStreamStatus((prev) => ({ ...prev, [svc]: 'reconnecting' }));
    };
    return () => {
      logStreamOpenCountRef.current = {};
      try {
        es.close();
      } catch {
        /* ignore */
      }
      if (logFlushRafRef.current != null) {
        cancelAnimationFrame(logFlushRafRef.current);
        logFlushRafRef.current = null;
      }
    };
  }, [activeLogTab, appendLogLines, base, mainTab, oauth.authenticated, oauth.configured, oauth.loading]);

  useEffect(() => {
    if (oauth.loading || (oauth.configured && !oauth.authenticated)) return undefined;
    if (activeLogTab !== 'traffic') return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        await loadTrafficLogs();
      } catch {
        if (!cancelled) {
          setLogStreamStatus((prev) => ({ ...prev, traffic: 'reconnecting' }));
        }
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [activeLogTab, loadTrafficLogs, oauth.authenticated, oauth.configured, oauth.loading]);

  useEffect(() => {
    if (oauth.loading || (oauth.configured && !oauth.authenticated)) return undefined;
    if (mainTab !== 'analytics') return undefined;
    const tick = async () => {
      try {
        await loadTrafficLogs(500);
      } catch {
        /* ignore */
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [loadTrafficLogs, mainTab, oauth.authenticated, oauth.configured, oauth.loading]);

  useEffect(() => {
    if (!overview?.host?.available) return;
    const label = new Date().toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    setMetricSeries((prev) =>
      [
        ...prev.slice(-47),
        {
          label,
          cpu: Number(overview.host.cpu_percent) || 0,
          ram: Number(overview.host.memory?.percent_used) || 0,
          disk: Number(overview.host.disk_root?.percent_used) || 0,
        },
      ].slice(-48)
    );
  }, [overview]);

  useEffect(() => {
    if (!logFollowScroll) return;
    if (logBoxRef.current) {
      logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    }
  }, [activeLogTab, containerLogs, logFollowScroll]);

  const services = useMemo(() => Array.isArray(overview?.services) ? overview.services : [], [overview]);

  const featureCounts = useMemo(() => buildFeatureRequestCounts(searches), [searches]);
  const preprocessSearches = useMemo(
    () => searches.filter((it) => resolveSearchBucket(it) === 'ai_preprocess').slice(0, 25),
    [searches],
  );
  const emailSearches = useMemo(
    () => searches.filter((it) => resolveSearchBucket(it) === 'email'),
    [searches],
  );

  const handleLogPanelScroll = useCallback(() => {
    const el = logBoxRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setLogFollowScroll(nearBottom);
  }, []);

  const resumeLogAutoscroll = useCallback(() => {
    setLogFollowScroll(true);
    requestAnimationFrame(() => {
      if (logBoxRef.current) {
        logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
      }
    });
  }, []);

  const adminUnlocked = !oauth.loading && (!oauth.configured || oauth.authenticated);
  const needGoogleSignIn = !oauth.loading && oauth.configured && !oauth.authenticated;

  return (
    <main className={`${ADMIN_SHELL} min-w-0 overflow-x-hidden p-4 sm:p-6`}>
      <div className="mx-auto max-w-7xl min-w-0 space-y-4">
        <header className={ADMIN_CARD}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-lg font-black tracking-tight text-slate-50">Admin Console (Dev Only)</h1>
              <p className="text-xs font-semibold text-slate-400">
                {oauth.loading
                  ? 'Checking access…'
                  : oauth.configured
                    ? oauth.authenticated
                      ? `Logged in as ${oauth.email || 'session'}`
                      : 'Protected with Google — sign-in required'
                    : 'Path: /admin · no Google SSO (set GOOGLE_OAUTH_* on the backend)'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {oauth.authenticated && oauth.configured && (
                <button
                  type="button"
                  onClick={() => {
                    window.location.href = `${base}/admin/oauth/logout`;
                  }}
                  className={ADMIN_BTN_GHOST}
                >
                  Logout
                </button>
              )}
              <button
                type="button"
                disabled={!adminUnlocked}
                onClick={refreshAll}
                className={ADMIN_BTN_PRIMARY}
              >
                Refresh
              </button>
              <button
                type="button"
                disabled={!adminUnlocked}
                onClick={runApiChecks}
                className={ADMIN_BTN_GHOST}
              >
                Check APIs
              </button>
            </div>
          </div>
          {error && <p className="mt-2 text-xs font-bold text-rose-300">{error}</p>}
        </header>

        {needGoogleSignIn && (
          <section className="rounded-2xl border border-cyan-500/30 bg-cyan-950/35 p-6 shadow-lg shadow-black/30">
            <h2 className="text-sm font-black uppercase tracking-[0.14em] text-cyan-200">Admin access</h2>
            <p className="mt-2 max-w-xl text-sm text-slate-300">
              This panel is available only after Google sign-in for the account authorized on the server.
            </p>
            <button
              type="button"
              onClick={() => {
                window.location.href = `${base}/admin/oauth/login`;
              }}
              className={`mt-4 ${ADMIN_BTN_PRIMARY}`}
            >
              Continue with Google
            </button>
          </section>
        )}

        {oauth.loading && (
          <p className={`${ADMIN_CARD} text-center text-xs font-bold text-slate-400`}>
            Loading access settings…
          </p>
        )}

        {!adminUnlocked ? null : (
          <Tabs.Root value={mainTab} onValueChange={setMainTab} className="w-full space-y-4">
            <Tabs.List className="flex flex-wrap gap-1 rounded-2xl border border-white/10 bg-slate-900/60 p-1.5 shadow-lg shadow-black/20 backdrop-blur-md">
              <Tabs.Trigger className={TAB_TRIGGER} value="overview">
                Overview
              </Tabs.Trigger>
              <Tabs.Trigger className={TAB_TRIGGER} value="analytics">
                Analytics
              </Tabs.Trigger>
              <Tabs.Trigger className={TAB_TRIGGER} value="ollama">
                Ollama
              </Tabs.Trigger>
              <Tabs.Trigger className={TAB_TRIGGER} value="nlp_lab">
                NLP & Testing Lab
              </Tabs.Trigger>
              <Tabs.Trigger className={TAB_TRIGGER} value="readability">
                Readability
              </Tabs.Trigger>
              <Tabs.Trigger className={TAB_TRIGGER} value="logs">
                Logs
              </Tabs.Trigger>
              <Tabs.Trigger className={TAB_TRIGGER} value="htop">
                HTOP
              </Tabs.Trigger>
              <Tabs.Trigger className={TAB_TRIGGER} value="nginx">
                Nginx
              </Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="overview" className="space-y-4 rounded-xl outline-none">
        <section className={ADMIN_CARD}>
          <h2 className={ADMIN_SECTION_TITLE}>Request Flow (Schematic)</h2>
          <div className="grid gap-2 text-center text-xs font-bold sm:grid-cols-9">
            <div className={`${ADMIN_CARD_MUTED} p-2`}>Client (Browser)</div>
            <div className="self-center text-slate-500">-&gt;</div>
            <div className="rounded-lg border border-cyan-500/30 bg-cyan-950/40 p-2 text-cyan-100">Frontend</div>
            <div className="self-center text-slate-500">-&gt;</div>
            <div className="rounded-lg border border-sky-500/30 bg-sky-950/40 p-2 text-sky-100">Backend API</div>
            <div className="self-center text-slate-500">-&gt;</div>
            <div className="rounded-lg border border-amber-500/30 bg-amber-950/35 p-2 text-amber-100">Redis / Celery</div>
            <div className="self-center text-slate-500">-&gt;</div>
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-950/35 p-2 text-emerald-100">SSE/JSON Response</div>
          </div>
        </section>

        <OpenSanctionsSyncMonitor baseUrl={base} />

        <AdminToolsHealthPanel />

        <AdminContainerControls
          onToast={setActionToast}
          onError={(msg) => setError(String(msg || ''))}
        />

        <section className="rounded-2xl border border-white/10 bg-slate-900/55 p-4 shadow-lg shadow-black/30 backdrop-blur-md">
          <h2 className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400">Service Status & Resources</h2>
          <div className="grid gap-3 md:grid-cols-5">
            {services.map((s) => {
              const workerBacklog = s.name === 'worker' && s.celery?.queue_backlog;
              return (
              <div
                key={s.name}
                className={`rounded-xl border p-3 ${
                  workerBacklog ? 'border-amber-400/50 bg-amber-950/40' : 'border-white/10 bg-slate-950/55'
                }`}
              >
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-base">{SERVICE_ICON[s.name] || '🔹'}</span>
                  <StatusPill status={s.status} />
                </div>
                <p className="text-sm font-black uppercase">{s.name}</p>
                <p className="text-[11px] text-slate-500">{s.notes || '-'}</p>
                {s.name === 'worker' && s.celery ? (
                  <p
                    className={`mt-1 text-[11px] font-bold ${
                      workerBacklog ? 'text-amber-200' : 'text-slate-300'
                    }`}
                  >
                    Active Tasks: {s.celery.active_tasks ?? 0} | Queued: {s.celery.reserved_tasks ?? 0}
                    {workerBacklog ? ' · queue backlog' : ''}
                  </p>
                ) : null}
                <p className="mt-1 text-[11px] text-slate-400">{s.description || ''}</p>
                {typeof s.ping_ms === 'number' && <p className="mt-1 text-[11px] font-bold text-slate-300">Ping: {s.ping_ms} ms</p>}
              </div>
            );
            })}
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <DashboardAreaChart title="CPU load" dataKey="cpu" series={metricSeries} stroke={CHART_COLORS.cpu} />
            <DashboardAreaChart title="RAM usage" dataKey="ram" series={metricSeries} stroke={CHART_COLORS.ram} />
            <DashboardAreaChart title="Disk usage" dataKey="disk" series={metricSeries} stroke={CHART_COLORS.disk} />
          </div>

          {overview?.host?.available ? (
            <div className="mt-4 rounded-xl border border-cyan-500/25 bg-cyan-950/30 p-3">
              <h3 className="mb-2 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-200">
                Host (htop-style view)
              </h3>
              <p className="mb-2 text-[10px] text-slate-400">
                Metrics reflect the namespace of the process serving this request (in Docker usually the{' '}
                <span className="font-black">backend</span> container, with cgroup limits). Charts above sample
                while this Admin page is open; per-service CPU/RAM since last container start is on the Containers
                panel. Python server:{' '}
                <span className="font-mono text-[10px]">{overview.host.python || '-'}</span>
              </p>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <HostMetricBar
                  label="CPU"
                  pct={overview.host.cpu_percent}
                  detail={`${overview.host.cpu_percent ?? '-'}% · ${overview.host.cpu_count ?? '-'} vCPU`}
                />
                <HostMetricBar
                  label="RAM"
                  pct={overview.host.memory?.percent_used}
                  detail={`${overview.host.memory?.percent_used ?? '-'}% · free ${Math.round(overview.host.memory?.available_mb || 0)} / total ${Math.round(overview.host.memory?.total_mb || 0)} MB`}
                />
                <HostMetricBar
                  label="Swap"
                  pct={overview.host.swap?.total_mb > 0 ? overview.host.swap?.percent_used : 0}
                  detail={
                    overview.host.swap?.total_mb > 0
                      ? `${overview.host.swap?.percent_used ?? '-'}% · ${Math.round(overview.host.swap?.total_mb || 0)} MB`
                      : 'No swap'
                  }
                />
                <HostMetricBar
                  label="Disk /"
                  pct={overview.host.disk_root?.percent_used}
                  detail={`${overview.host.disk_root?.percent_used ?? '-'}% · ${overview.host.disk_root?.used_gb ?? '-'} / ${overview.host.disk_root?.total_gb ?? '-'} GB`}
                />
              </div>
              {overview.host.loadavg && (
                <p className="mt-2 font-mono text-[11px] font-bold text-slate-300">
                  Load avg: {overview.host.loadavg['1m']} {overview.host.loadavg['5m']} {overview.host.loadavg['15m']}{' '}
                  <span className="ml-2 font-sans text-[10px] font-semibold text-slate-500">
                    Uptime: {formatUptimeSeconds(overview.host.uptime_s)}
                  </span>
                </p>
              )}
              {!overview.host.loadavg && (
                <p className="mt-2 text-[11px] font-bold text-slate-300">
                  Uptime: {formatUptimeSeconds(overview.host.uptime_s)}{' '}
                  <span className="text-[10px] font-semibold text-slate-500">(load n/a on this OS)</span>
                </p>
              )}
              <div className="mt-4 overflow-hidden rounded-lg border border-white/10 bg-slate-950">
                <table className="w-full border-collapse text-left font-mono text-[10px] text-slate-200">
                  <thead>
                    <tr className="border-b border-slate-800 bg-slate-900/80 text-[9px] font-black uppercase tracking-widest text-slate-500">
                      <th className="px-2 py-2">PID</th>
                      <th className="px-2 py-2">Process</th>
                      <th className="px-2 py-2">Container</th>
                      <th className="px-2 py-2 text-right">RSS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(overview.host.top_processes_mem || []).map((row, idx) => (
                      <tr key={`${row.pid}-${idx}`} className="border-b border-slate-800/80 last:border-0">
                        <td className="px-2 py-1.5 text-emerald-400">{row.pid}</td>
                        <td className="max-w-[140px] truncate px-2 py-1.5 text-slate-300" title={row.name}>
                          {row.name}
                        </td>
                        <td className="max-w-[160px] truncate px-2 py-1.5 text-sky-300" title={row.container}>
                          {row.container ?? '—'}
                        </td>
                        <td className="px-2 py-1.5 text-right text-amber-200">{row.rss_mb} MB</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!overview.host.top_processes_mem || overview.host.top_processes_mem.length === 0) && (
                  <div className="px-2 py-4 text-center text-slate-500">—</div>
                )}
              </div>
            </div>
          ) : (
            overview?.host && (
              <p className="mt-3 text-[11px] text-amber-200">
                Host metrics unavailable: {overview.host.reason || 'unknown'}
              </p>
            )
          )}

          <div className="mt-3 flex items-center justify-center gap-2 text-xs font-black text-slate-400">
            <span>frontend</span><span>&lt;-&gt;</span><span>backend</span><span>&lt;-&gt;</span><span>redis</span><span>&lt;-&gt;</span><span>worker</span><span>&lt;-&gt;</span><span>telegram_bot</span>
          </div>
          <div className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
            <div className="rounded-lg bg-slate-950/50 p-2">PID worker: <span className="font-black">{overview?.resource?.pid ?? '-'}</span></div>
            <div className="rounded-lg bg-slate-950/50 p-2">CPU cores (OS): <span className="font-black">{overview?.resource?.cpu_count ?? '-'}</span></div>
            <div className="rounded-lg bg-slate-950/50 p-2">
              Overview response time: <span className="font-black">{overview?.resource?.overview_ms ?? '-'} ms</span>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <span className="rounded-full border border-white/15 bg-slate-950/60 px-2.5 py-1 text-[10px] font-bold text-slate-400">
              Redis DB Size:{' '}
              {overview?.redis_meta?.available === false
                ? '—'
                : overview?.redis_meta?.dbsize ?? '—'}{' '}
              keys active
            </span>
            {overview?.redis_meta?.available ? (
              <span className="text-[10px] font-semibold text-slate-500">
                admin:last_searches {overview.redis_meta.admin_last_searches_len ?? 0} · admin:events_log{' '}
                {overview.redis_meta.admin_events_log_len ?? 0}
              </span>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              onClick={toggleGlobalCache}
              className={`rounded-lg border px-3 py-2 text-[11px] font-black uppercase tracking-wider ${
                cacheEnabled
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                  : 'border-red-300 bg-red-50 text-red-800'
              }`}
            >
              Cache Global: {cacheEnabled ? 'ON' : 'OFF'}
            </button>
            <button
              type="button"
              disabled={isDestructiveLocked('redis-db')}
              onClick={() => clearRedis('db')}
              className={`${DESTRUCTIVE_BTN} border-amber-300 bg-amber-50 text-amber-200`}
            >
              Clear Redis Cache (DB)
            </button>
            <button
              type="button"
              disabled={isDestructiveLocked('redis-all')}
              onClick={() => clearRedis('all')}
              className={`${DESTRUCTIVE_BTN} border-red-300 bg-red-50 text-red-800`}
            >
              Clear Redis (ALL)
            </button>
            <button
              type="button"
              disabled={isDestructiveLocked('purge:sse:*')}
              onClick={() => purgeNamespace('sse:*')}
              className={`${DESTRUCTIVE_BTN} border-cyan-500/30 bg-cyan-950/40 text-cyan-200`}
            >
              Purge sse:*
            </button>
            <button
              type="button"
              disabled={isDestructiveLocked('purge:search:*')}
              onClick={() => purgeNamespace('search:*')}
              className={`${DESTRUCTIVE_BTN} border-cyan-400/35 bg-cyan-950/40 text-cyan-100`}
            >
              Purge search:*
            </button>
            <button
              type="button"
              disabled={isDestructiveLocked('purge:censys:*')}
              onClick={() => purgeNamespace('censys:*')}
              className={`${DESTRUCTIVE_BTN} border-violet-300 bg-violet-50 text-violet-800`}
            >
              Purge censys:*
            </button>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            <span className="font-black">Clear Cache (DB)</span> removes only the current Redis database used by the app.
            <span className="mx-1">·</span>
            <span className="font-black">Clear All</span> wipes every Redis database on the instance.
            <span className="mx-1">·</span>{' '}
            Destructive actions run immediately; each button stays dimmed ~{DESTRUCTIVE_COOLDOWN_MS / 1000}s after success.
          </p>
          <p className="mt-1 text-[11px] text-slate-400">
            Per request bypass is available via <span className="font-black">?no_cache=1</span> on scan endpoints.
          </p>
        </section>

        <section className="rounded-2xl border border-white/10 bg-slate-900/55 p-5 shadow-sm">
          <h2 className="mb-4 text-xs font-black uppercase tracking-[0.18em] text-slate-500">API checks (ENV)</h2>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full min-w-[640px] border-collapse text-left text-xs">
              <thead className="border-b border-white/10 bg-slate-900 text-[10px] font-black uppercase tracking-[0.14em] text-slate-300">
                <tr>
                  <th className="px-3 py-2.5">Provider</th>
                  <th className="px-3 py-2.5">Validity</th>
                  <th className="px-3 py-2.5">Latency</th>
                  <th className="px-3 py-2.5">Quota / Credits Available</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(checks || {}).map(([provider, entry]) => (
                  <tr key={provider} className="border-b border-slate-100 bg-slate-950/50/80 last:border-b-0">
                    <td className="px-3 py-2.5">
                      <div className="font-black text-slate-200">{provider}</div>
                      <div className="mt-0.5 font-mono text-[10px] text-slate-500">
                        masked: {entry?.masked || 'not_set'}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <ApiCheckPill ok={!!entry?.check?.ok} />
                      {entry?.check?.reason ? (
                        <div className="mt-1 max-w-[14rem] truncate font-mono text-[10px] text-slate-500">
                          {entry.check.reason}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[11px] text-slate-400">
                      {apiCheckLatencyLabel(entry?.check)}
                    </td>
                    <td className="px-3 py-2.5">
                      <ApiQuotaCell quota={entry?.quota} provider={provider} />
                    </td>
                  </tr>
                ))}
                {Object.keys(checks || {}).length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-sm text-slate-400">
                      Run “Check APIs” from the header.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
            </Tabs.Content>

            <Tabs.Content value="analytics" className="space-y-4 rounded-xl outline-none">
              <AdminParallelSearchesCard />
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
                {[
                  { id: 'name', label: 'Name intelligence', sub: '/aggregate-stream-name*', val: featureCounts.name },
                  { id: 'email', label: 'Email', sub: '/aggregate-email-stream', val: featureCounts.email },
                  { id: 'phone', label: 'Phone', sub: '/aggregate-phone-stream', val: featureCounts.phone },
                  {
                    id: 'due',
                    label: 'Due diligence',
                    sub: '/aggregate-company-stream · /aggregate-corporate-stream',
                    val: featureCounts.due_diligence,
                  },
                  {
                    id: 'ai_preprocess',
                    label: 'AI preprocess',
                    sub: 'POST /search/preprocess (Ollama / spaCy)',
                    val: featureCounts.ai_preprocess,
                  },
                ].map((c) => (
                  <div
                    key={c.id}
                    className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 to-slate-950/60 p-6 shadow-sm"
                  >
                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{c.label}</p>
                    <p className="mt-3 text-4xl font-black tabular-nums tracking-tight text-slate-50">{c.val}</p>
                    <p className="mt-2 text-[11px] leading-snug text-slate-500">{c.sub}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-cyan-500/25 bg-cyan-950/30 p-4 text-sm leading-relaxed text-slate-300">
                Unified storage in the Redis distributed cache (
                <span className="font-semibold text-cyan-100">admin:last_searches</span>
                ). Analytics counters reflect only records from this history — no double-counting from
                traffic-log. For email scans, open an <span className="font-semibold">email</span> row and
                mark platforms as <span className="font-semibold">Correct</span> /{' '}
                <span className="font-semibold">False positive</span> /{' '}
                <span className="font-semibold">Should be registered</span>.
              </div>
              {featureCounts.other > 0 && (
                <p className="text-xs text-slate-500">
                  Other routes (outside standard buckets):{' '}
                  <span className="font-bold">{featureCounts.other}</span>
                </p>
              )}

              <div className="rounded-2xl border border-white/10 bg-slate-900/55 p-5 shadow-sm">
                <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <h2 className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Recent searches</h2>
                    <p className="mt-1 text-xs text-slate-500">
                      {emailSearches.length} email scan{emailSearches.length === 1 ? '' : 's'} in history — click a row
                      to review probe accuracy.
                    </p>
                  </div>
                </div>
                <div className="max-h-[480px] overflow-auto rounded-lg border border-slate-100">
                  <table className="w-full border-collapse text-left text-xs">
                    <thead className="sticky top-0 bg-slate-950">
                      <tr className="border-b border-white/10 text-[10px] font-black uppercase tracking-wider text-slate-500">
                        <th className="px-3 py-2">Time</th>
                        <th className="px-3 py-2">Path</th>
                        <th className="px-3 py-2">Bucket</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searches.map((it, i) => {
                        const pathLabel = it.path || it.full_url || '';
                        const bucket = resolveSearchBucket(it);
                        const rowKey = it.id || `${it.ts}-${i}`;
                        const isSelected = selectedSearch?.id === it.id && selectedSearch?.ts === it.ts;
                        return (
                          <tr
                            key={rowKey}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedSearch(it)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedSearch(it);
                              }
                            }}
                            className={`cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-800 hover:text-slate-100 ${
                              isSelected ? 'bg-slate-800 text-slate-100' : ''
                            }`}
                          >
                            <td className="whitespace-nowrap px-3 py-2 font-mono text-[10px] text-inherit opacity-80">
                              {it.ts}
                            </td>
                            <td className="max-w-lg px-3 py-2 font-semibold text-inherit" title={pathLabel}>
                              <span className="line-clamp-2">{pathLabel}</span>
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                                  isSelected
                                    ? 'bg-slate-700 text-slate-200'
                                    : 'bg-slate-950/60 text-slate-300'
                                }`}
                              >
                                {bucket}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {searches.length === 0 && (
                    <p className="py-10 text-center text-sm text-slate-400">No searches recorded yet.</p>
                  )}
                </div>
              </div>
            </Tabs.Content>
            <AdminSearchDetailDrawer search={selectedSearch} onClose={() => setSelectedSearch(null)} />

            <Tabs.Content value="ollama" className="min-w-0 space-y-4 overflow-x-hidden rounded-xl outline-none">
              <AdminOllamaPanel preprocessSearches={preprocessSearches} />
            </Tabs.Content>

            <Tabs.Content value="nlp_lab" className="space-y-4 rounded-xl outline-none">
              <AdminNlpLab />
            </Tabs.Content>

            <Tabs.Content value="readability" className="space-y-4 rounded-xl outline-none">
              <AdminReadabilityPanel />
            </Tabs.Content>

            <Tabs.Content value="logs" className="space-y-4 rounded-xl outline-none">
              <AdminFrontendSearchTraces />
              <div className="rounded-2xl border border-white/10 bg-slate-900/55 p-5 shadow-sm">
                <DockerHeader />
                <ContainerStats
                  logServices={logServices}
                  containerLogs={containerLogs}
                  logStreamStatus={logStreamStatus}
                  activeLogTab={activeLogTab}
                  onActiveLogTabChange={setActiveLogTab}
                />
                <LogConsole
                  logBoxRef={logBoxRef}
                  activeLogTab={activeLogTab}
                  containerLogs={containerLogs}
                  logFollowScroll={logFollowScroll}
                  onResumeAutoscroll={resumeLogAutoscroll}
                  onLogPanelScroll={handleLogPanelScroll}
                />
              </div>
            </Tabs.Content>

            <Tabs.Content value="htop" className="space-y-4 rounded-xl outline-none">
              <AdminHtopPanel />
            </Tabs.Content>

            <Tabs.Content value="nginx" className="space-y-4 rounded-xl outline-none">
              <AdminNginxPanel />
            </Tabs.Content>
          </Tabs.Root>
        )}
      </div>
      {actionToast ? (
        <div
          role="status"
          className="fixed bottom-4 left-1/2 z-50 max-w-xl -translate-x-1/2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-900 shadow-lg"
        >
          {actionToast}
        </div>
      ) : null}
      {loading && (
        <div className="fixed bottom-4 right-4 rounded-full border border-white/10 bg-slate-900/55 px-3 py-1 text-xs font-black shadow-sm">
          Updating...
        </div>
      )}
    </main>
  );
}

